import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Eyebrow, DisplayHeading } from "@/components/editorial";
import {
  addPolicyLink,
  checkAllPolicyLinks,
  listPolicyLinks,
  setPolicyLinkRetired,
} from "@/lib/admin.policy-links.functions";

export const Route = createFileRoute("/admin/policy-links")({
  head: () => ({
    meta: [
      { title: "Policy links — Tiger's Eye admin" },
      { name: "description", content: "Manage links to universities' AI, integrity and referencing pages." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Policy links — Tiger's Eye admin" },
      { property: "og:description", content: "Manage and check university policy links." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PolicyLinksPage,
});

const KINDS = [
  { value: "ai", label: "AI use" },
  { value: "integrity", label: "Academic integrity" },
  { value: "referencing", label: "Referencing" },
] as const;
type Kind = (typeof KINDS)[number]["value"];

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "Never";

function statusBadge(s: string) {
  if (s === "ok") return <Badge variant="secondary">Working</Badge>;
  if (s === "redirected") return <Badge variant="outline">Moved — check</Badge>;
  if (s === "broken") return <Badge variant="destructive">Broken</Badge>;
  return <Badge variant="outline">Not checked</Badge>;
}

function PolicyLinksPage() {
  const qc = useQueryClient();
  const list = useServerFn(listPolicyLinks);
  const add = useServerFn(addPolicyLink);
  const retire = useServerFn(setPolicyLinkRetired);
  const checkAll = useServerFn(checkAllPolicyLinks);
  const q = useQuery({ queryKey: ["admin", "policy-links"], queryFn: () => list() });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "policy-links"] });

  const addM = useMutation({
    mutationFn: (v: { institutionId: string; kind: Kind; label: string; url: string }) => add({ data: v }),
    onSuccess: () => { toast.success("Link added and checked"); refresh(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const retireM = useMutation({
    mutationFn: (v: { id: string; retired: boolean }) => retire({ data: v }),
    onSuccess: () => refresh(),
    onError: (e: Error) => toast.error(e.message),
  });
  const checkM = useMutation({
    mutationFn: () => checkAll(),
    onSuccess: (r) => {
      r.broken ? toast.warning(`${r.broken} of ${r.checked} links are broken`) : toast.success(`All ${r.checked} links checked`);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const links = q.data?.links ?? [];
  const brokenCount = links.filter((l) => !l.retired && l.status === "broken").length;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>University policies</Eyebrow>
          <DisplayHeading level={2} className="mt-1">Policy links</DisplayHeading>
          <p className="text-sm text-ink-secondary mt-2 max-w-2xl">
            Links to each university's own pages only — we never copy policy text. Labels are written by us.
          </p>
        </div>
        <Button onClick={() => checkM.mutate()} disabled={checkM.isPending || links.length === 0}>
          {checkM.isPending ? "Checking…" : "Check all links now"}
        </Button>
      </div>

      {brokenCount > 0 && (
        <p role="alert" className="border border-destructive text-destructive p-3 text-sm">
          {brokenCount} link{brokenCount === 1 ? " is" : "s are"} broken. Replace or retire {brokenCount === 1 ? "it" : "them"}.
        </p>
      )}
      {q.isLoading && <p className="text-ink-secondary">Loading…</p>}
      {q.error && <p role="alert" className="text-destructive">{(q.error as Error).message}</p>}

      {(q.data?.institutions ?? []).map((inst) => (
        <section key={inst.id} className="border border-border bg-card p-5 space-y-4">
          <h3 className="font-serif text-xl text-primary">
            {inst.name} <span className="text-xs font-mono text-ink-tertiary">{inst.domain}</span>
          </h3>
          {KINDS.map((k) => {
            const rows = links.filter((l) => l.institution_id === inst.id && l.kind === k.value);
            return (
              <div key={k.value} className="space-y-2">
                <p className="text-sm font-medium">{k.label}</p>
                {rows.length === 0 && <p className="text-xs text-ink-tertiary">No link yet.</p>}
                {rows.map((l) => (
                  <div key={l.id} className={`flex flex-wrap items-center gap-2 text-sm ${l.retired ? "opacity-50" : ""}`}>
                    {statusBadge(l.status)}
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-primary underline break-all">{l.label}</a>
                    <span className="text-xs text-ink-tertiary">
                      Checked {fmt(l.last_checked)}{l.http_status ? ` · ${l.http_status}` : ""}
                      {l.status === "redirected" && l.final_url ? ` · now at ${l.final_url}` : ""}
                      {l.check_error ? ` · ${l.check_error}` : ""}
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => retireM.mutate({ id: l.id, retired: !l.retired })}>
                      {l.retired ? "Restore" : "Retire"}
                    </Button>
                  </div>
                ))}
                <AddLinkForm
                  pending={addM.isPending}
                  onAdd={(label, url) => addM.mutate({ institutionId: inst.id, kind: k.value, label, url })}
                  domain={inst.domain}
                />
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

function AddLinkForm({ onAdd, pending, domain }: { onAdd: (label: string, url: string) => void; pending: boolean; domain: string | null }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  return (
    <form
      className="flex flex-wrap gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!label.trim() || !url.trim()) return;
        onAdd(label.trim(), url.trim());
        setLabel(""); setUrl("");
      }}
    >
      <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Our label" className="max-w-xs" aria-label="Link label" />
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`https://www.${domain ?? "university.ac.uk"}/…`} className="max-w-md" type="url" aria-label="Link address" />
      <Button type="submit" size="sm" variant="outline" disabled={pending}>Add</Button>
    </form>
  );
}
